import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// ActionCreators / Selectors

import {
    changePage,
    changeData,
    clearFormSubmission,
    clearMethods,
    deleteForm,
    makeFormSnapshot,
    registerForm,
    saveSsap,
    setMethods,
    switchHyperReview,
    submitForm,
    validateForm,
    updateCachedSSN,
    updateCachedIMI,
} from '@actions/ssap/ssapActionCreators';

import {
    getCachedData,
    getServerDate,
    selectIfMedicaidTerminationFeatureEnabled,
    selectIfShouldInvokeInlineEligibility,
    selectIfSogiFeatureEnabled,
    selectIsInlineCurrentIncomeEnabled,
    selectMedicaidEndDateOverride,
} from '@reducers/appReducer';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { executeCallbacks } from '@utils/miscellaneous/executeCallbacks';
import { getApplicantGuids, getPayloadForIMIUpdate, getPayloadForSsnUpdate, maskPII, mixValue, performCleanUpAndChecks } from '@utils/ssap/form-utils';
import { getCurrentAndMaxAchievedPages } from '@utils/miscellaneous/getCurrentAndMaxAchievedPages';
import { isEqual } from '@utils/miscellaneous/objectHelpers';
import { localStorageHyperDashboardTools } from '@utils/tools/localStorageHyperDashboardTools';
import { preventerTools } from '@utils/tools/preventerTools';
import { processJsonTools } from '@utils/tools/processJsonTools';
import { processUiProperties } from '@utils/tools/processUiProperties';
import { updateDirtyFlags } from '@utils/miscellaneous/updateDirtyFlags';
import { updateImiValues, updateSsnValues } from '@utils/householdMember/updateMaskedValues';

const Form = props => {
    // Props from Parent Component (usually it is Page)

    const {
        topLevelValidity,
        formName,
        pageName = ''
    } = props;

    // Props from Redux Store

    const cachedData = useSelector(getCachedData);
    const invokeInlineEligibility = useSelector(selectIfShouldInvokeInlineEligibility);
    const inlineCurrentIncomeEnabled = useSelector(selectIsInlineCurrentIncomeEnabled);
    const isSogiFeatureEnabled = useSelector(selectIfSogiFeatureEnabled);
    const medicaidTerminationFeatureEnabled = useSelector(selectIfMedicaidTerminationFeatureEnabled);
    const serverDate = useSelector(getServerDate);
    const medicaidEndDateOverride = useSelector(selectMedicaidEndDateOverride);

    const isFormValidationShown = localStorageHyperDashboardTools.getValueByPropName('isFormValidationShown', false);
    const isInvalidInputsShown = localStorageHyperDashboardTools.getValueByPropName('isInvalidInputsShown', false);

    const {
        data,
        data: {
            ssapApplicationId,
            maxAchievedPage
        },
        dataSnapshot,
        form,
        form: {
            [formName]: {
                isEligiblyForSave,
                isFormSubmitted,
                isNonStandaloneInputChanged,
                registeredInputs,
            } = {
                registeredInputs: [],
                snapshot: ''
            }
        },
        environment: {
            impersonation
        },
        hyperReview,
        onLocalFormExit,
        onLocalFormSubmit,
        modals,
        page,
        structure: {
            bloodRelationshipStructure,
            taxHouseholdStructure
        },
        formClasses,
        formProps = {}
    } = props;

    const [ localState, setState ] = useState({
        isGoingToExitWithoutSaving: false,
        frozenData: cloneObject(data),
    });

    const [ registration, updateRegistered ] = useState({
        registered: [],
        isRegistrationAccomplished: false
    });

    const { registered, isRegistrationAccomplished } = registration;

    const adjustToSnapshot = values => {
        return values.map(({ inputId, initialInputValue }) => ({
            inputId,
            value: initialInputValue
        }));
    };

    // Up to date primitive of registered inputs values to add as dependency for watching

    const registeredInputsToString = registeredInputs.map(input => input.valueOf()).toString();
    const registeredInputsToStringByNames = registeredInputs.map(input => input.inputName).toString();
    const registeredInputsValidityToStrings = registeredInputs.map(input => input.validOf()).toString();
    const registeredNonStandaloneInputs = registeredInputs.reduce(
        (inputs, input) => {
            const {
                isInputStandalone
            } = input;

            return isInputStandalone
                ? inputs
                : [ ...inputs, input ];
        },
        [ ]
    );

    const registeredNonStandaloneInputsToStrings = registeredNonStandaloneInputs.map(
        input => !input.isInputStandalone && input.inputValue
    ).toString();

    useEffect(() => {
        if(!form[formName]) {
            props.actions.registerForm(formName);
        }
        return () => {
            props.actions.deleteForm(formName);
        };
    }, [ formName ]);

    useEffect(() => {
        if(isRegistrationAccomplished) {
            props.actions.makeFormSnapshot(formName, JSON.stringify(registered));
        }
    }, [ isRegistrationAccomplished ]);

    useEffect(() => {
        if(registration.registered.length < registeredInputs.length && !isRegistrationAccomplished)  {
            updateRegistered(() => ({
                registered: adjustToSnapshot(registeredInputs),
                isRegistrationAccomplished: true
            }));
        }
    }, [ registeredInputsToStringByNames ]);

    useEffect(() => {
        props.actions.setMethods(formName, {
            onFormSubmit,
            onFormExit,
            onFormSubmitForValidationOnly
        });
        return () => {
            props.actions.clearMethods(formName);
        };
    }, [
        isEligiblyForSave,
        isNonStandaloneInputChanged,
        maxAchievedPage,
        registeredInputsToString,
        (onLocalFormExit ? modals.length : '')
    ]);

    // Scrolling to the first error message on Form submit

    useEffect(() => {
        if(isFormSubmitted && !isEligiblyForSave) {
            const [ element ] = [ ...document.getElementsByClassName('error--msg') ]
                .filter(message => message.className.match(/shown error--msg/));

            if(element) {
                element.scrollIntoView({ behavior: 'smooth' });
            } else {
                console.log('Error message has not been found on the page');
            }
        }
    }, [ isFormSubmitted ]);

    // Watching for changes in inputs and auto-validation Form

    useEffect(() => {
        props.actions.validateForm(checkIfEligible(), formName);
    }, [
        registeredInputsValidityToStrings,
        registeredNonStandaloneInputsToStrings,
        topLevelValidity
    ]);

    useEffect(() => {
        isFormSubmitted && isEligiblyForSave && props.actions.clearFormSubmission(formName);
    }, [isEligiblyForSave]);

    const getBody = (destinationPage, bypassedData, dataToUpdate) => {

        const dataToSave = updateImiValues(
            updateSsnValues(
                bypassedData ? bypassedData : data,
                mixValue(cachedData.ssns, dataToUpdate.ssnToUpdate)
            ),
            cachedData.immigrationInfo
        );

        const resolvedData = updateDirtyFlags(
            dataToSave,
            dataSnapshot,
            cachedData
        );

        const areDiffsInHhms = !isEqual(
            getApplicantGuids(resolvedData),
            getApplicantGuids(localState.frozenData),
        );

        const {
            shouldRestrictNavigation,
            cleanedData
        } = performCleanUpAndChecks({
            dataAfterChanges: resolvedData,
            dataBeforeChanges: localState.frozenData,
            isSogiFeatureEnabled,
            medicaidTerminationFeatureEnabled,
            pageName,
            serverDate,
            medicaidEndDateOverride
        });

        const inconsistencyCriteria = [
            areDiffsInHhms,
            isNonStandaloneInputChanged,
            shouldRestrictNavigation
        ];

        const isDataPotentiallyInconsistent = inconsistencyCriteria.some(criteria => criteria);

        return {
            application: JSON.stringify(
                processUiProperties({
                    ...processJsonTools.convertTaxHousehold(
                        processJsonTools.convertBloodRelationships(
                            maskPII(cleanedData),
                            bloodRelationshipStructure
                        ),
                        taxHouseholdStructure
                    ),
                    ...getCurrentAndMaxAchievedPages(
                        destinationPage,
                        page,
                        maxAchievedPage,
                        isDataPotentiallyInconsistent,
                    )
                })
            ),
            applicationId: ssapApplicationId,
            csrOverride: impersonation,
            recalculateIncome: !!bypassedData,
            invokeInlineEligibility: inlineCurrentIncomeEnabled ? invokeInlineEligibility : false,
            ...dataToUpdate
        };
    };

    const onFormSubmitForValidationOnly = (successCallbacks, failureCallbacks) => {
        props.actions.submitForm(formName);
        executeCallbacks(isEligiblyForSave ? successCallbacks : failureCallbacks);
    };

    const onFormSubmit = (destinationPage, callbacks, exceptionAction, bypassedData) => {

        const {
            failCallback
        } = preventerTools.provideObject(callbacks && callbacks.find(cb => typeof cb === 'object'));

        props.actions.submitForm(formName);

        if (isEligiblyForSave) {
            if(onLocalFormSubmit) {
                onLocalFormSubmit(data);
                return null;
            }

            if (!exceptionAction) {
                const updateCallBacks = () => {

                    bypassedData && props.actions.clearFormSubmission(formName);
                    const mergeHyperReviewCallback = () => hyperReview ? [ () => props.actions.switchHyperReview(false) ] : [];

                    if (Array.isArray(callbacks)) {
                        return [
                            ...callbacks,
                            ...mergeHyperReviewCallback()
                        ];
                    } else {
                        return [
                            ...mergeHyperReviewCallback()
                        ];
                    }
                };

                const { ssnToUpdate } = getPayloadForSsnUpdate(data, localState, pageName);
                const { immigrationInfoToUpdate } = getPayloadForIMIUpdate(data, localState, pageName);

                if (ssnToUpdate) {
                    props.actions.updateCachedSSN(
                        ssnToUpdate.applicantGuid,
                        ssnToUpdate.ssn
                    );
                }
                if (immigrationInfoToUpdate) {
                    const { applicantGuid, ...restProps } = immigrationInfoToUpdate;
                    props.actions.updateCachedIMI(
                        applicantGuid,
                        restProps
                    );
                }

                // TODO: added temporarily. Remove before pushing to Master
                const areConsolesShown = localStorageHyperDashboardTools
                    .getValueByPropName('areNonStandaloneConsolesShown', false);

                if (isNonStandaloneInputChanged) {
                    areConsolesShown && console.log(
                        `At least one non-standalone input has been changed: 
                        User is forced to continue application going page by page!`
                    );
                } else {
                    areConsolesShown && console.log(
                        `Only standalone inputs might be changed: 
                        We still do not block jumping!`
                    );
                }

                props.actions.saveSsap(
                    updateCallBacks(),
                    getBody(destinationPage, bypassedData, { ssnToUpdate, immigrationInfoToUpdate }),
                );
            } else {
                exceptionAction();
            }
        } else {
            executeCallbacks(failCallback);
        }
    };

    const onFormExit = (callbacks, bypassedData = undefined) => {

        if (onLocalFormExit) {
            onLocalFormExit(localState.frozenData);
            return null;
        }

        props.actions.changeData(
            maskPII({
                ...(bypassedData ? bypassedData : localState.frozenData),
                maxAchievedPage,
            }),
        );

        setState(state => ({
            ...state,
            isGoingToExitWithoutSaving: true,
        }));

        executeCallbacks(callbacks);
    };

    const checkIfEligible = () => {
        if (registeredInputs.length === 0) {
            return {
                areAllInputsValid: true,
                isEligiblyForSave: true,
                isFormOnTopLevelValid: true,
                isNonStandaloneInputChanged: false,
                nonStandaloneInputsChanged: []
            };
        }
        const isApprovedByTopLevel = topLevelValidity === undefined ? true : topLevelValidity;

        let result = true;

        registeredInputs.forEach(input => {
            if (input.inputIsValid === false || (input.isInputRequired && (input.inputValue === '' || input.inputValue === null))) {
                isInvalidInputsShown && console.log(input);
                result = false;
            }
        });

        const areConsolesShown = localStorageHyperDashboardTools
            .getValueByPropName('areNonStandaloneConsolesShown', false);

        areConsolesShown && console.log({ registeredNonStandaloneInputs });

        const nonStandaloneInputsChanged = registeredInputs
            .reduce(
                (inputs, input) => {
                    const { isInputStandalone, initialInputValue, inputValue } = input;

                    return !isInputStandalone && inputValue !== initialInputValue
                        ? [ ...inputs, input ]
                        : inputs;
                },
                [ ]
            );

        areConsolesShown && console.log({ nonStandaloneInputsChanged });

        return {
            isEligiblyForSave: isApprovedByTopLevel && result,
            isFormOnTopLevelValid: isApprovedByTopLevel,
            isNonStandaloneInputChanged: nonStandaloneInputsChanged.length > 0,
            areAllInputsValid: result,
            nonStandaloneInputsChanged: nonStandaloneInputsChanged
        };
    };

    const updateReactChildren = children => {
        return React.Children.map(children, child => {
            if (!React.isValidElement(child)) {
                return child;
            }
            let childProps = {};
            if (
                React.isValidElement(child) &&
                (typeof child.props.inputData === 'object' || child.props.underFormControl === true)
            ) {
                childProps = {
                    parentForm: {
                        name: formName
                    }
                };
            }
            childProps.children = updateReactChildren(child.props.children);
            return React.cloneElement(child, childProps);
        });
    };

    return (
        <form autoComplete="off" className={`${formClasses && formClasses.join(' ')}`} {...formProps}>
            { form[formName] && updateReactChildren(props.children) }
        </form>
    );
};

const mapStateToProps = (state, ownProps) => ({

    environment: state.common.environment,
    modals: state.common.modals,

    data: ownProps.data ? ownProps.data : state.ssap.data,
    dataSnapshot: state.ssap.dataSnapshot,
    form: state.ssap.form,
    hyperReview: state.ssap.hyperReview,
    page: state.ssap.page,
    structure: state.ssap.structure
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            changeData,
            changePage,
            clearFormSubmission,
            clearMethods,
            deleteForm,
            makeFormSnapshot,
            registerForm,
            saveSsap,
            setMethods,
            submitForm,
            switchHyperReview,
            updateCachedIMI,
            updateCachedSSN,
            validateForm,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Form);
